import { Card } from '@buyerflo/ui/components/Card'
import { Field, FormSection } from '@buyerflo/ui/components/FormSection'
import { Badge } from '@buyerflo/ui/components/Badge'
import { useMutation, useQuery } from '@tanstack/react-query'
import Button from 'components/button'
import CopyButton from 'components/CopyButton'
import Loading from 'components/Loading'
import { EmailIcon, ShareIcon } from 'components/icons'
import { connectStatusLabel, connectStatusTone, fmtAud } from 'affiliate/shared'
import { SizableText, View, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'

const StatTile = ({ label, value }: { label: string; value: string }) => (
    <Card padded flex={1} minWidth={140}>
        <SizableText fontSize={11} textTransform="uppercase" letterSpacing={0.4} color="$colorMuted">{label}</SizableText>
        <SizableText fontSize={22} fontWeight="700">{value}</SizableText>
    </Card>
)

export default function Overview() {
    const trpc = useTRPC()
    const affiliateQuery = useQuery(trpc.affiliate.getMyAffiliate.queryOptions())
    const earningsQuery = useQuery(trpc.affiliate.getMyEarnings.queryOptions())
    const referralsQuery = useQuery(trpc.affiliate.getMyReferrals.queryOptions())
    const connectQuery = useQuery(trpc.affiliate.getConnectStatus.queryOptions())
    const onboardMutation = useMutation(trpc.affiliate.createConnectOnboardingLink.mutationOptions())

    if (affiliateQuery.isLoading) return <Loading />
    const affiliate = affiliateQuery.data
    if (!affiliate) return <SizableText>Could not load your affiliate profile.</SizableText>

    const link = affiliate.referralLink ?? ''
    const earnings = earningsQuery.data
    const referrals = referralsQuery.data ?? []
    const connect = connectQuery.data
    const activeReferrals = referrals.filter(r => r.status === 'active').length

    // Don't show "$0.00 owed" / "0 referrals" while these still-loading (or errored) — that reads
    // like the commission vanished. Show a placeholder until the real figures are available.
    const earningsReady = !earningsQuery.isLoading && !earningsQuery.isError
    const referralsReady = !referralsQuery.isLoading && !referralsQuery.isError
    const money = (cents?: number) => (earningsReady ? fmtAud(cents ?? 0) : '—')

    const shareText = `Sign up to Buyerflo with my link and get ${affiliate.discountPercent}% off for ${affiliate.discountDurationMonths} months: ${link}`

    const startOnboarding = async () => {
        const { url } = await onboardMutation.mutateAsync()
        window.location.href = url
    }

    return (
        <YStack gap="$4">
            {/* Hero: the referral link is the centrepiece */}
            <FormSection title="Share your link">
                <SizableText color="$colorMuted" size="$2">
                    Your contacts get {affiliate.discountPercent}% off for {affiliate.discountDurationMonths} months. You
                    earn {affiliate.commissionPercent}% commission on what they pay.
                </SizableText>
                <Field label="Referral link">
                    <YStack gap="$2">
                        <SizableText
                            size="$4"
                            backgroundColor="$backgroundHover"
                            padding="$3"
                            borderRadius="$3"
                            selectable
                        >
                            {link}
                        </SizableText>
                        <XStack gap="$2" flexWrap="wrap">
                            <CopyButton text={link} label="Copy link" copiedLabel="Link copied" />
                            <CopyButton text={affiliate.promoCode} label="Copy code" variant="outlined" />
                            <Button
                                variant="outlined"
                                icon={<EmailIcon size={16} />}
                                onPress={() => {
                                    window.location.href = `mailto:?subject=${encodeURIComponent(
                                        'Try Buyerflo',
                                    )}&body=${encodeURIComponent(shareText)}`
                                }}
                            >
                                Email
                            </Button>
                            {typeof navigator !== 'undefined' && (navigator as any).share && (
                                <Button
                                    variant="outlined"
                                    icon={<ShareIcon size={16} />}
                                    onPress={() =>
                                        (navigator as any).share({ title: 'Buyerflo', text: shareText, url: link })
                                    }
                                >
                                    Share
                                </Button>
                            )}
                        </XStack>
                    </YStack>
                </Field>
            </FormSection>

            {/* Stats */}
            <XStack gap="$3" flexWrap="wrap">
                <StatTile label="Active referrals" value={referralsReady ? String(activeReferrals) : '—'} />
                <StatTile label="Total earned" value={money(earnings?.lifetimeCents)} />
                <StatTile label="Pending payout" value={money(earnings?.unpaidCents)} />
                <StatTile label="Paid to date" value={money(earnings?.paidToDateCents)} />
            </XStack>

            {/* Payout setup */}
            <FormSection
                title="Payouts"
                action={
                    connect ? (
                        <Badge tone={connectStatusTone[connect.status] ?? 'neutral'}>
                            {connectStatusLabel[connect.status] ?? connect.status}
                        </Badge>
                    ) : undefined
                }
            >
                <SizableText color="$colorMuted" size="$2">
                    {connect?.complete
                        ? 'Your payout account is connected. Commission is transferred automatically each month.'
                        : 'Set up payouts to receive your commission. We use Stripe to securely collect your bank details.'}
                </SizableText>
                {affiliate.payoutFailed && (
                    <SizableText size="$2" color="$statusDanger">
                        Your last payout failed{affiliate.payoutFailureReason ? `: ${affiliate.payoutFailureReason}` : ''}.
                        Please check your bank details via Manage payouts.
                    </SizableText>
                )}
                <View>
                    <Button
                        loading={onboardMutation.isPending}
                        onPress={startOnboarding}
                    >
                        {connect?.complete ? 'Manage payouts' : 'Set up payouts'}
                    </Button>
                </View>
            </FormSection>
        </YStack>
    )
}
